import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ContrattiPage from "./pages/ContrattiPage";
import ContrattoPage from "./pages/ContrattoPage";
import CapitolatiPage from "./pages/CapitolatiPage";
import CapitolatoPage from "./pages/CapitolatoPage";
import FornitoriPage from "./pages/FornitoriPage";
import AlboPage from "./pages/AlboPage";
import GarePage from "./pages/GarePage";
import GaraPage from "./pages/GaraPage";
import OffertaPage from "./pages/OffertaPage";
import ComparazionePage from "./pages/ComparazionePage";
import AnalisiPage from "./pages/AnalisiPage";
import StoricoPage from "./pages/StoricoPage";

function Caricamento() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="spinner w-12 h-12" />
    </div>
  );
}

function Protetta({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Caricamento />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Guardia di sezione: stesso principio di manutenzioni, ma qui rimanda a una pagina che lo dice,
 * invece che alla home — se l'Ufficio Acquisti perdesse una sezione, un redirect silenzioso alla
 * lista contratti sembrerebbe un bug del link, non un permesso mancante.
 */
function Sezione({ children, sezione }) {
  const { canAccessSection } = useAuth();
  if (canAccessSection(sezione)) return children;

  return (
    <div className="card max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900">
        Sezione non disponibile
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Il tuo ruolo non ha la sezione <code>{sezione}</code>. Si concede dalla
        matrice dei permessi, in <em>Amministrazione → Permessi ruoli</em> del
        portale manutenzioni.
      </p>
    </div>
  );
}

/**
 * La home e' la prima sezione che il ruolo ha. Non e' sempre l'archivio contratti: la Direzione,
 * per esempio, arriva per approvare le analisi e non ha la sezione contratti di acquisti.
 */
const ORDINE_HOME = [
  ["acquisti_contratti", "/contratti"],
  ["acquisti_gare", "/gare"],
  ["acquisti_analisi", "/analisi"],
  ["acquisti_capitolati", "/capitolati"],
  ["acquisti_fornitori", "/fornitori"],
];

function Home() {
  const { canAccessSection } = useAuth();
  const prima = ORDINE_HOME.find(([sezione]) => canAccessSection(sezione));
  return <Navigate to={prima ? prima[1] : "/contratti"} replace />;
}

function NonTrovata() {
  return (
    <div className="card max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900">
        Pagina non trovata
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        L'indirizzo richiesto non esiste in questo portale.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/*"
        element={
          <Protetta>
            <Layout>
              <Routes>
                <Route
                  path="/"
                  element={<Home />}
                />

                <Route
                  path="/contratti"
                  element={
                    <Sezione sezione="acquisti_contratti">
                      <ContrattiPage />
                    </Sezione>
                  }
                />
                <Route
                  path="/contratti/nuovo"
                  element={
                    <Sezione sezione="acquisti_contratti">
                      <ContrattoPage />
                    </Sezione>
                  }
                />
                <Route
                  path="/contratti/:id"
                  element={
                    <Sezione sezione="acquisti_contratti">
                      <ContrattoPage />
                    </Sezione>
                  }
                />

                <Route
                  path="/capitolati"
                  element={
                    <Sezione sezione="acquisti_capitolati">
                      <CapitolatiPage />
                    </Sezione>
                  }
                />
                <Route
                  path="/capitolati/:id"
                  element={
                    <Sezione sezione="acquisti_capitolati">
                      <CapitolatoPage />
                    </Sezione>
                  }
                />

                <Route
                  path="/gare"
                  element={
                    <Sezione sezione="acquisti_gare">
                      <GarePage />
                    </Sezione>
                  }
                />
                <Route
                  path="/gare/:id"
                  element={
                    <Sezione sezione="acquisti_gare">
                      <GaraPage />
                    </Sezione>
                  }
                />
                <Route
                  path="/gare/:id/offerte/:offertaId"
                  element={
                    <Sezione sezione="acquisti_gare">
                      <OffertaPage />
                    </Sezione>
                  }
                />

                <Route
                  path="/gare/:id/comparazione"
                  element={
                    <Sezione sezione="acquisti_analisi">
                      <ComparazionePage />
                    </Sezione>
                  }
                />
                <Route
                  path="/analisi"
                  element={
                    <Sezione sezione="acquisti_analisi">
                      <AnalisiPage />
                    </Sezione>
                  }
                />
                <Route
                  path="/storico"
                  element={
                    <Sezione sezione="acquisti_analisi">
                      <StoricoPage />
                    </Sezione>
                  }
                />

                <Route
                  path="/fornitori"
                  element={
                    <Sezione sezione="acquisti_fornitori">
                      <FornitoriPage />
                    </Sezione>
                  }
                />
                <Route
                  path="/albo"
                  element={
                    <Sezione sezione="acquisti_fornitori">
                      <AlboPage />
                    </Sezione>
                  }
                />

                <Route path="*" element={<NonTrovata />} />
              </Routes>
            </Layout>
          </Protetta>
        }
      />
    </Routes>
  );
}
